import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dpsRequests, type DpsRequest, type DpsRequestForm } from '@/api/dpsRequests';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';

/**
 * Кабінет ДПС → «створити»: сервісний запит/форма до ДПС.
 *
 * Після вибору форми відображається БЛАНК за офіційною формою (J1300208 —
 * Додаток 1 наказу МФУ №416): одержувач (ДПІ, авто з картки організації),
 * інформація про платника (HNAME/HTIN, авто), період «з 01.<міс>.<рік> ДО
 * <дата>» (HDATE1 = кінець періоду) та спосіб отримання витягу (M01–M05,
 * 3 секції як у формі). Створити → підписати КЕП → відправити (KepSignPanel).
 * Відповідь (витяг) приходить квитанцією №2 у журнал Кабінету ДПС.
 */

const MONTHS = ['', 'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
const now = new Date();

// Спосіб отримання витягу — M01–M05 за офіційною формою (3 секції).
const RECEIVE_SECTIONS: {
  title: string;
  options: { code: 'M01' | 'M02' | 'M03' | 'M04' | 'M05'; label: string }[];
}[] = [
  {
    title: 'В електронній формі із накладенням кваліфікованого електронного '
      + 'підпису керівника (заступника керівника) територіального органу ДПС:',
    options: [
      { code: 'M01', label: 'узагальнюючі показники за платежами' },
      { code: 'M02', label: 'показники в розрізі платежів' },
    ],
  },
  {
    title: 'У паперовій формі з особистим підписом керівника (заступника '
      + 'керівника) територіального органу ДПС:',
    options: [
      { code: 'M03', label: 'узагальнюючі показники за платежами' },
      { code: 'M04', label: 'показники в розрізі платежів' },
    ],
  },
  {
    title: 'В електронній формі з накладенням кваліфікованої печатки ДПС:',
    options: [
      { code: 'M05', label: 'з розрахунком сум пені (пп. 129.1.1 та 129.1.3 '
        + 'п. 129.1 ст. 129 ПКУ)' },
    ],
  },
];

export default function DpsRequestCreatePage() {
  const navigate = useNavigate();
  const { id: rawId } = useParams();  // /dps-cabinet/requests/:id — перегляд/підпис
  const id = rawId && rawId !== 'new' ? rawId : undefined;  // 'new' = створення
  const [search, setSearch] = useState('');
  const [formCode, setFormCode] = useState('');
  const [orgId, setOrgId] = useState('');
  // Період за формою: «з 01.<місяць>.<рік> до <дата>». Дефолт — з початку
  // поточного року до сьогодні (типовий запит витягу).
  const [month, setMonth] = useState(1);
  const [year, setYear] = useState(now.getFullYear());
  const [periodEnd, setPeriodEnd] = useState(now.toISOString().slice(0, 10));
  const [docDate, setDocDate] = useState(now.toISOString().slice(0, 10));
  const [receiveMode, setReceiveMode] =
    useState<'M01' | 'M02' | 'M03' | 'M04' | 'M05'>('M01');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<DpsRequest | null>(null);

  const formsQ = useQuery({ queryKey: ['dps-request-forms'], queryFn: dpsRequests.forms });
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  // Режим перегляду існуючого запиту (з журналу) — завантажити і показати
  // панель підпису (можна повернутись і підписати пізніше).
  useEffect(() => {
    if (!id) return;
    dpsRequests.get(id).then(setCreated).catch(() => setError('Запит не знайдено'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const filteredForms = useMemo<DpsRequestForm[]>(() => {
    const q = search.trim().toLowerCase();
    const all = formsQ.data ?? [];
    if (!q) return all;
    return all.filter((f) =>
      f.title.toLowerCase().includes(q)
      || f.code.toLowerCase().includes(q)
      || f.f_code.toLowerCase().includes(q));
  }, [formsQ.data, search]);

  const selectedForm = (formsQ.data ?? []).find((f) => f.code === formCode) ?? null;
  const effOrgId = orgId || availableOrgs[0]?.id || '';
  const selectedOrg = orgList.data?.items.find((o) => o.id === effOrgId) ?? null;
  const isFop = selectedOrg?.entity_type === 'FOP';
  const payerTin = (isFop ? (selectedOrg?.inn || selectedOrg?.edrpou)
    : selectedOrg?.edrpou) ?? '';

  async function create() {
    setError(null);
    if (!formCode) { setError('Оберіть форму'); return; }
    if (!effOrgId) { setError('Оберіть організацію'); return; }
    try {
      const req = await dpsRequests.create({
        organization_id: effOrgId,
        form_code: formCode,
        document_date: `${docDate}T00:00:00Z`,
        period_month: month,
        period_year: year,
        period_end_date: periodEnd,
        receive_mode: receiveMode,
      });
      setCreated(req);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка створення');
    }
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Новий запит до ДПС</h2>
        <button onClick={() => navigate('/dps-cabinet')}>до кабінету</button>
      </div>

      {!created ? (
        <>
          {/* Крок 1: форма (пошук за назвою/кодом) */}
          <div className="field" style={{ maxWidth: 720 }}>
            <label>Форма (пошук за назвою або кодом)</label>
            <input value={search} placeholder="напр. «стан розрахунків» або J1300208"
              onChange={(e) => { setSearch(e.target.value); }} />
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, marginTop: 4,
              maxHeight: 220, overflow: 'auto' }}>
              {(filteredForms.length ? filteredForms : []).map((f) => (
                <div key={f.code}
                  onClick={() => setFormCode(f.code)}
                  style={{ padding: '0.45rem 0.6rem', cursor: 'pointer',
                    background: formCode === f.code ? '#eff6ff' : undefined,
                    borderBottom: '1px solid #f1f5f9' }}>
                  <b>{f.code}</b> / {f.f_code} — {f.title}
                </div>
              ))}
              {formsQ.data && filteredForms.length === 0 && (
                <div style={{ padding: '0.5rem', color: 'var(--muted)' }}>
                  Нічого не знайдено.
                </div>
              )}
            </div>
          </div>

          {/* Крок 2: організація */}
          <div className="field" style={{ maxWidth: 420, marginTop: '0.75rem' }}>
            <label>Організація *</label>
            <select value={effOrgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">—</option>
              {orgList.data?.items.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          </div>

          {/* Крок 3: бланк за офіційною формою */}
          {selectedForm && effOrgId && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8,
              padding: '1rem', marginTop: '0.75rem', maxWidth: 860 }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap',
                justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div className="field" style={{ flex: '1 1 340px' }}>
                  <label title="Змінити — на картці організації, вкладка «Коди»">
                    Найменування територіального органу ДПС (одержувач)
                  </label>
                  <input disabled style={{ background: '#f8fafc' }}
                    value={selectedOrg?.sti_name ?? ''}
                    placeholder="не заповнено — картка організації → «Коди»" />
                </div>
                <div className="field" style={{ width: 150 }}>
                  <label>Дата запиту</label>
                  <DatePopover value={docDate} onChange={setDocDate} />
                </div>
              </div>

              <div style={{ textAlign: 'center', fontWeight: 600,
                margin: '0.5rem 0 0.75rem' }}>
                Запит щодо отримання витягу з інформаційно-комунікаційної системи
                ДПС про стан розрахунків платника за податками, зборами, платежами
                та єдиним внеском
              </div>

              {/* Період: «з 01.<міс>.<рік> до <дата>» (HDATE1 = кінець періоду) */}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
                flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <span>період з «01»</span>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                  {MONTHS.slice(1).map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <span>року до</span>
                <div style={{ width: 150 }}>
                  <DatePopover value={periodEnd} onChange={setPeriodEnd} />
                </div>
              </div>

              {/* Інформація про платника (авто з організації) */}
              <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 6,
                padding: '0.6rem 0.75rem', marginBottom: '0.75rem' }}>
                <legend style={{ fontSize: '0.85rem', color: 'var(--muted)',
                  padding: '0 0.4rem' }}>Інформація про платника</legend>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div className="field" style={{ flex: '1 1 340px' }}>
                    <label>Найменування / ПІБ</label>
                    <input disabled style={{ background: '#f8fafc' }}
                      value={selectedOrg?.full_name || selectedOrg?.name || ''} />
                  </div>
                  <div className="field" style={{ width: 180 }}>
                    <label>Податковий номер</label>
                    <input disabled style={{ background: '#f8fafc' }}
                      value={payerTin} />
                  </div>
                </div>
              </fieldset>

              {/* Спосіб отримання витягу — M01–M05 (3 секції, як у формі) */}
              <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                Форма отримання витягу
              </div>
              {RECEIVE_SECTIONS.map((sec) => (
                <div key={sec.title} style={{ marginBottom: '0.6rem' }}>
                  <div style={{ fontSize: '0.83rem', color: 'var(--muted)',
                    marginBottom: '0.25rem' }}>{sec.title}</div>
                  {sec.options.map((opt) => (
                    <label key={opt.code} style={{ display: 'flex', gap: '0.4rem',
                      alignItems: 'baseline', cursor: 'pointer',
                      padding: '0.1rem 0 0.1rem 0.75rem' }}>
                      <input type="radio" name="receive_mode" value={opt.code}
                        checked={receiveMode === opt.code}
                        onChange={() => setReceiveMode(opt.code)} />
                      <span><b>{opt.code}</b> — {opt.label}</span>
                    </label>
                  ))}
                </div>
              ))}

              <p style={{ color: 'var(--muted)', fontSize: '0.82rem',
                margin: '0.75rem 0 0' }}>
                Відповідь надійде в електронній формі (квитанція №2 у Кабінеті ДПС)
                — зазвичай до 2 робочих днів.
              </p>
            </div>
          )}

          <div style={{ marginTop: '0.75rem' }}>
            <button className="primary" onClick={create} disabled={!formCode || !effOrgId}>
              створити запит
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac',
            borderRadius: 6, padding: '0.5rem 0.75rem', margin: '0.5rem 0' }}>
            ✓ Запит {created.document_number} створено (період з
            01.{String(created.period_month).padStart(2, '0')}.{created.period_year}
            {created.period_end_date ? ` до ${created.period_end_date}` : ''},
            форма отримання {created.receive_mode}). Зберігся чернеткою у
            Кабінеті ДПС — там підпишіть КЕП і відправте.
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <button className="primary" onClick={() => navigate('/dps-cabinet')}>
              до Кабінету ДПС →
            </button>
          </div>
        </>
      )}

      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginTop: '0.75rem' }}>{error}</div>
      )}
    </div>
  );
}
